import { useEffect, useRef, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import { useIsAllowedToCreateWhiteboard } from '@confluence/content-types-utils/entry-points/useIsAllowedToCreateWhiteboard.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import type { ContentTypeCreationEligibilityRef } from '@confluence/fabric-extension-lib/entry-points/extension-provider.ts';

import type {
	useContentTypeCreationEligibilityQuery$data,
	useContentTypeCreationEligibilityQuery,
} from './__generated__/useContentTypeCreationEligibilityQuery.graphql.ts';

export const useContentTypeCreationEligibility = (
	spaceKey?: string,
	shouldCheck = true,
): ContentTypeCreationEligibilityRef => {
	const eligibilityRef = useRef<ContentTypeCreationEligibilityRef['current']>(undefined);
	const isAllowedToCreateWhiteboard = useIsAllowedToCreateWhiteboard();
	const environment = useRelayEnvironment();
	const [queryData, setQueryData] = useState<
		{ spaceKey: string; data: useContentTypeCreationEligibilityQuery$data } | undefined
	>();

	useEffect(() => {
		setQueryData(undefined);
		if (
			!shouldCheck ||
			!spaceKey ||
			(!isExperimentEnabled('platform_editor_slash_content_type_eligibility') &&
				!isExperimentEnabled('platform_editor_slash_command'))
		) {
			return;
		}
		let active = true;
		const subscription = fetchQuery<useContentTypeCreationEligibilityQuery>(
			environment,
			graphql`
				query useContentTypeCreationEligibilityQuery(
					$spaceKey: String!
					$includeEligibility: Boolean!
				) {
					space(key: $spaceKey) {
						key
						spaceTypeSettings {
							enabledContentTypes @include(if: $includeEligibility) {
								isWhiteboardsEnabled
								isDatabasesEnabled
							}
						}
						operations @include(if: $includeEligibility) {
							operation
							targetType
						}
					}
				}
			`,
			{ spaceKey, includeEligibility: true },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				if (active) {
					setQueryData({ spaceKey, data });
				}
			},
			error: () => {
				if (active) {
					setQueryData(undefined);
				}
			},
		});
		return () => {
			active = false;
			subscription.unsubscribe();
		};
	}, [
		environment,
		shouldCheck,
		spaceKey,
		// oxlint-disable-next-line react-hooks/exhaustive-deps -- Experiment calls must stay inline for feature-gate static analysis.
		shouldCheck &&
			(isExperimentEnabled('platform_editor_slash_content_type_eligibility') ||
				isExperimentEnabled('platform_editor_slash_command')),
	]);

	if (!shouldCheck) {
		eligibilityRef.current = { spaceKey, whiteboard: false, database: false };
		return eligibilityRef;
	}

	if (
		!isExperimentEnabled('platform_editor_slash_content_type_eligibility') &&
		!isExperimentEnabled('platform_editor_slash_command')
	) {
		eligibilityRef.current = undefined;
		return eligibilityRef;
	}

	const space = queryData?.data.space;
	const hasValidSpaceResult = Boolean(
		spaceKey && queryData?.spaceKey === spaceKey && space?.key === spaceKey,
	);
	const canCreatePage =
		hasValidSpaceResult &&
		Boolean(
			space?.operations?.some(
				(operation) =>
					operation?.operation === OperationTypes.CREATE && operation.targetType === 'page',
			),
		);
	const enabledContentTypes = space?.spaceTypeSettings?.enabledContentTypes;

	eligibilityRef.current = {
		spaceKey,
		whiteboard:
			canCreatePage &&
			isAllowedToCreateWhiteboard &&
			Boolean(enabledContentTypes?.isWhiteboardsEnabled) &&
			isContentTypeEnabledInCurrentEnv('whiteboard') &&
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained for whiteboard creation eligibility.
			!fg('whiteboards_compliance_opt_out'),
		database:
			canCreatePage &&
			Boolean(enabledContentTypes?.isDatabasesEnabled) &&
			isContentTypeEnabledInCurrentEnv('database') &&
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained for database creation eligibility.
			!fg('confluence_frontend_databases_opt-out'),
	};

	return eligibilityRef;
};
